import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getApiBaseUrl } from '@/lib/api'
import { getAuthHeaders } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	ResponsiveContainer,
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Line,
	Bar,
	Legend,
	PieChart,
	Pie,
	Cell,
} from 'recharts'
import type { TooltipProps } from 'recharts'

type FeedbackByType = {
	bugReports: number
	featureRequests: number
	reviews: number
	surveyResponses: number
}

const FEEDBACK_TYPE_META = [
	{ key: 'bugReports' as const, label: 'Bug reports', color: '#ef4444' },
	{ key: 'featureRequests' as const, label: 'Feature requests', color: '#8b5cf6' },
	{ key: 'reviews' as const, label: 'Reviews', color: '#f59e0b' },
	{ key: 'surveyResponses' as const, label: 'Survey responses', color: '#3b82f6' },
] satisfies ReadonlyArray<{ key: keyof FeedbackByType; label: string; color: string }>

const PERIODS = ['7d', '30d', '90d', '365d'] as const

type TimelinePoint = {
	day: string
	signups: number
	widgetsCreated: number
	signupEmails?: string[]
	widgetNames?: string[]
}

function TimelineTooltip({ active, payload, label }: TooltipProps<number, string>) {
	if (!active || !payload?.length) return null
	const point = payload[0]?.payload as TimelinePoint | undefined
	if (!point) return null
	const emails = point.signupEmails || []
	const widgetNames = point.widgetNames || []

	return (
		<div className="rounded-md border bg-background p-3 text-xs shadow-md">
			<div className="mb-2 font-medium">{String(label)}</div>
			<div className="space-y-1">
				{payload.map((entry) => (
					<div key={entry.dataKey} className="flex items-center justify-between gap-3">
						<span className="text-muted-foreground">{entry.name}</span>
						<span className="font-medium">{entry.value ?? 0}</span>
					</div>
				))}
			</div>
			{emails.length > 0 ? (
				<div className="mt-3">
					<div className="font-medium">Signups emails</div>
					<ul className="mt-1 space-y-0.5 text-muted-foreground">
						{emails.map((email) => (
							<li key={email}>{email}</li>
						))}
					</ul>
				</div>
			) : null}
			{widgetNames.length > 0 ? (
				<div className="mt-3">
					<div className="font-medium">Widgets created</div>
					<ul className="mt-1 space-y-0.5 text-muted-foreground">
						{widgetNames.map((name, index) => (
							<li key={`${name}-${index}`}>{name}</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	)
}

export default function AdminAnalyticsPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const period = (searchParams.get('period') as (typeof PERIODS)[number]) || '30d'

	const { data, isLoading } = useQuery({
		queryKey: ['admin-analytics', period],
		queryFn: async () => {
			const response = await fetch(`${getApiBaseUrl()}/api/admin/analytics?period=${period}`, {
				headers: await getAuthHeaders(),
			})
			if (!response.ok) throw new Error('Failed to load admin analytics')
			return response.json()
		},
	})

	const timeline = useMemo(() => (data?.timeline || []) as TimelinePoint[], [data])

	const feedbackByType = data?.feedbackByType as FeedbackByType | undefined

	const feedbackPieData = useMemo(() => {
		if (!feedbackByType) return []
		return FEEDBACK_TYPE_META.map((row) => ({
			name: row.label,
			value: feedbackByType[row.key] ?? 0,
			color: row.color,
		})).filter((d) => d.value > 0)
	}, [feedbackByType])

	const feedbackTotal = useMemo(() => {
		if (!feedbackByType) return 0
		return FEEDBACK_TYPE_META.reduce((sum, row) => sum + (feedbackByType[row.key] ?? 0), 0)
	}, [feedbackByType])

	const feedbackLegendRows = useMemo(() => {
		if (!feedbackByType) return []
		return [...FEEDBACK_TYPE_META]
			.map((row) => ({ ...row, n: feedbackByType[row.key] ?? 0 }))
			.sort((a, b) => b.n - a.n || a.label.localeCompare(b.label))
	}, [feedbackByType])

	return (
		<div className="space-y-6">
			<div className="flex justify-end">
				<div className="flex gap-2">
					{PERIODS.map((value) => (
						<button
							key={value}
							onClick={() => setSearchParams({ period: value })}
							className={`rounded-md px-3 py-1 text-xs ${
								period === value ? 'bg-primary text-primary-foreground' : 'border'
							}`}
						>
							{value}
						</button>
					))}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				<Card className="lg:col-span-2">
					<CardHeader>
						<CardTitle>Signups & widgets timeline</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="h-[340px]">
							{isLoading ? (
								<div className="text-sm text-muted-foreground">Loading...</div>
							) : (
								<ResponsiveContainer width="100%" height="100%">
									<ComposedChart data={timeline}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="day" tickFormatter={(v) => String(v).slice(5)} />
										<YAxis allowDecimals={false} />
										<Tooltip content={<TimelineTooltip />} />
										<Legend />
										<Bar dataKey="widgetsCreated" name="Widgets created" fill="#10b981" />
										<Line type="monotone" dataKey="signups" name="New signups" stroke="#4f46e5" />
									</ComposedChart>
								</ResponsiveContainer>
							)}
						</div>
					</CardContent>
				</Card>

				<Card className="lg:col-span-1">
					<CardHeader>
						<CardTitle>Feedback by type</CardTitle>
						<p className="text-sm text-muted-foreground">
							Counts for the selected period ({feedbackTotal} total)
						</p>
					</CardHeader>
					<CardContent>
						{isLoading ? (
							<div className="text-sm text-muted-foreground">Loading...</div>
						) : !feedbackByType ? (
							<div className="text-sm text-muted-foreground">No data</div>
						) : (
							<div className="space-y-5">
								<div className="mx-auto h-[260px] w-full max-w-[280px]">
									{feedbackTotal === 0 ? (
										<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
											No feedback in this period
										</div>
									) : (
										<ResponsiveContainer width="100%" height="100%">
											<PieChart>
												<Pie
													data={feedbackPieData}
													dataKey="value"
													nameKey="name"
													cx="50%"
													cy="50%"
													innerRadius={62}
													outerRadius={98}
													paddingAngle={2}
													labelLine={false}
													label={({ name, value, percent }) =>
														`${name}: ${value} (${((percent ?? 0) * 100).toFixed(0)}%)`
													}
												>
													{feedbackPieData.map((entry) => (
														<Cell key={entry.name} fill={entry.color} />
													))}
												</Pie>
												<Tooltip
													formatter={(value: number) => [value, 'Count']}
													contentStyle={{ borderRadius: 8 }}
												/>
											</PieChart>
										</ResponsiveContainer>
									)}
								</div>
								<ul className="space-y-2 text-sm">
									{feedbackLegendRows.map((row) => (
										<li key={row.key} className="flex items-center justify-between gap-4">
											<span className="flex items-center gap-2">
												<span
													className="h-2.5 w-2.5 shrink-0 rounded-full"
													style={{ backgroundColor: row.color }}
												/>
												{row.label}
											</span>
											<span className="tabular-nums font-medium">{row.n}</span>
										</li>
									))}
								</ul>
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	)
}
